import { analyticsEvents } from "@healthos/analytics";
import { useEffect } from "react";
import { analytics } from "../lib/analytics";
import { setDatadogContextProperty } from "../lib/datadog";

/*
 * Retention, as a number on every event.
 *
 * The question is "how many members come back on day 1, day 2, day 3, and
 * where does it fall off". Answering it needs a day-zero that survives a new
 * browser, so it is anchored to the server's onboarding completion timestamp
 * rather than anything the client stores.
 *
 * Two things happen per session. `days_since_onboarding` is set as RUM global
 * context, so every action that session carries it and any behaviour can be
 * grouped into a return curve. Then one `appDayOpened` per member per local
 * day gives the plain "did they open it at all" curve.
 */

const RETURN_DAY_KEY = "thrive_return_day";

/** The local calendar date, as the stable key for "already counted today". */
function localDateKey(now: Date): string {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * Whole days between two instants, counted as local calendar days rather than
 * elapsed hours. Finishing onboarding at 11pm and returning at 8am the next
 * morning is day 1, not day 0, which is how anyone reading the curve will
 * think about it. Rounding absorbs the 23 and 25 hour days at a DST boundary.
 *
 * Returns null for an unparseable timestamp so a bad value is dropped rather
 * than reported as day zero.
 */
export function daysSinceLocalDate(fromIso: string, now: Date): number | null {
  const from = new Date(fromIso);
  if (Number.isNaN(from.getTime())) return null;
  const startOfDay = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const days = Math.round((startOfDay(now) - startOfDay(from)) / 86_400_000);
  // A clock behind the server's would otherwise report a negative day.
  return Math.max(0, days);
}

export function useReturnTracking(params: {
  userId: string | null;
  /** Server-authoritative day zero. Absent until the member has onboarded. */
  onboardingCompletedAt: string | null | undefined;
}): void {
  const { userId, onboardingCompletedAt } = params;

  useEffect(() => {
    if (!userId || !onboardingCompletedAt) return;
    const now = new Date();
    const daysSinceOnboarding = daysSinceLocalDate(onboardingCompletedAt, now);
    if (daysSinceOnboarding === null) return;

    setDatadogContextProperty("days_since_onboarding", daysSinceOnboarding);

    // Keyed by member as well as day: a shared browser must not let one
    // person's visit stand in for another's.
    const storageKey = `${RETURN_DAY_KEY}:${userId}`;
    const today = localDateKey(now);
    let lastCounted: string | null = null;
    try {
      lastCounted = localStorage.getItem(storageKey);
    } catch {
      // localStorage unavailable; fall through and count the open. An
      // over-count of opens is a better failure than a silent gap in the curve.
    }
    if (lastCounted === today) return;
    try {
      localStorage.setItem(storageKey, today);
    } catch {
      // localStorage unavailable
    }

    analytics.track(analyticsEvents.appDayOpened, {
      days_since_onboarding: daysSinceOnboarding,
      is_first_day: daysSinceOnboarding === 0,
    });
  }, [userId, onboardingCompletedAt]);
}
